import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import { colors, fonts } from "../styles/colors";
import { useExternalLink } from "../hooks/useExternalLink";

const Nav = styled.nav`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: bold;
  font-family: ${fonts.heading};
  color: ${colors.inkBlack};
  text-decoration: none;

  &:hover {
    color: ${colors.dustyRose};
  }
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${colors.inkBlack};
  text-decoration: none;
  font-weight: 500;
  font-family: ${fonts.body};
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.dustyRose};
  }

  &.active {
    color: ${colors.inkBlack};
    font-weight: bold;
  }
`;

const ExternalLink = styled.button`
  color: ${colors.inkBlack};
  text-decoration: none;
  font-weight: 500;
  font-family: ${fonts.body};
  transition: color 0.3s ease;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    color: ${colors.dustyRose};
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  background: none;
  border: none;
  color: ${colors.inkBlack};
  font-weight: 500;
  font-family: ${fonts.body};
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.dustyRose};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid ${colors.paleMauve};
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  min-width: 250px;
  z-index: 1000;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transform: translateY(${(props) => (props.isOpen ? "0" : "-10px")});
  transition: all 0.3s ease;
  padding: 0.5rem 0;
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 0.75rem 1rem;
  color: ${colors.inkBlack};
  text-decoration: none;
  font-weight: 500;
  font-family: ${fonts.body};
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.paleMauve};
    color: ${colors.inkBlack};
  }
`;

const DropdownExternalItem = styled.button`
  display: block;
  padding: 0.75rem 1rem;
  color: ${colors.inkBlack};
  text-decoration: none;
  font-weight: 500;
  font-family: ${fonts.body};
  transition: all 0.3s ease;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  width: 100%;
  text-align: left;

  &:hover {
    background: ${colors.paleMauve};
    color: ${colors.inkBlack};
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${colors.inkBlack};

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.isOpen ? "block" : "none")};
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(10px);
    padding: 1rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
`;

const MobileNavLink = styled(Link)`
  display: block;
  padding: 1rem;
  color: ${colors.inkBlack};
  text-decoration: none;
  font-weight: 500;
  font-family: ${fonts.body};
  border-bottom: 1px solid ${colors.paleMauve};

  &:hover {
    background: ${colors.paleMauve};
    color: ${colors.inkBlack};
  }

  &.active {
    color: ${colors.inkBlack};
    font-weight: bold;
    background: ${colors.paleMauve};
  }
`;

const MobileNavButton = styled.button`
  display: block;
  padding: 1rem;
  color: ${colors.inkBlack};
  text-decoration: none;
  font-weight: 500;
  font-family: ${fonts.body};
  border-bottom: 1px solid ${colors.paleMauve};
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  width: 100%;
  text-align: left;

  &:hover {
    background: ${colors.paleMauve};
    color: ${colors.inkBlack};
  }
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const handleExternalLink = useExternalLink();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const closeDropdown = () => {
    setDropdownOpen(false);
  };

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About Shana" },
    {
      path: "https://0c1c94-91.myshopify.com/products/poem-club",
      label: "Poem Club",
      external: true,
    },
    { path: "/books", label: "Books" },
    {
      label: "Work With Shana",
      children: [
        { path: "/events", label: "Book Live Poetry" },
        {
          path: "https://0c1c94-91.myshopify.com/products/custom-poems",
          label: "Custom poems",
          external: true,
        },
        { path: "/product/custom-speeches", label: "Custom Speeches" },
      ],
    },
    { path: "/contact", label: "Contact" },
    {
      path: "https://calendly.com/supergirlreject",
      label: "Meet Shana in NYC",
      external: true,
    },
  ];

  return (
    <Nav>
      <NavContainer>
        <Logo to="/">Shana Marie Roark</Logo>

        <NavMenu>
          {navItems.map((item, index) => (
            <li key={index}>
              {item.children ? (
                <DropdownContainer
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <DropdownButton onClick={toggleDropdown}>
                    {item.label}
                    <FaChevronDown />
                  </DropdownButton>
                  <DropdownMenu isOpen={dropdownOpen}>
                    {item.children.map((child, childIndex) => (
                      <div key={childIndex}>
                        {child.external ? (
                          <DropdownExternalItem
                            as="button"
                            onClick={() => {
                              handleExternalLink(
                                child.path,
                                child.label,
                                "navbar_dropdown"
                              );
                              closeDropdown();
                            }}
                          >
                            {child.label}
                          </DropdownExternalItem>
                        ) : (
                          <DropdownItem to={child.path} onClick={closeDropdown}>
                            {child.label}
                          </DropdownItem>
                        )}
                      </div>
                    ))}
                  </DropdownMenu>
                </DropdownContainer>
              ) : item.external ? (
                <ExternalLink
                  as="button"
                  onClick={() =>
                    handleExternalLink(item.path, item.label, "navbar_main")
                  }
                >
                  {item.label}
                </ExternalLink>
              ) : (
                <NavLink
                  to={item.path}
                  className={location.pathname === item.path ? "active" : ""}
                >
                  {item.label}
                </NavLink>
              )}
            </li>
          ))}
        </NavMenu>

        <MobileMenuButton onClick={toggleMenu}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </MobileMenuButton>
      </NavContainer>

      <MobileMenu isOpen={isOpen}>
        {navItems.map((item, index) => (
          <div key={index}>
            {item.children ? (
              <div>
                <MobileNavLink
                  className="dropdown-header"
                  onClick={() => toggleDropdown()}
                >
                  {item.label} <FaChevronDown />
                </MobileNavLink>
                {dropdownOpen &&
                  item.children.map((child, childIndex) =>
                    child.external ? (
                      <MobileNavButton
                        key={childIndex}
                        onClick={() => {
                          handleExternalLink(
                            child.path,
                            child.label,
                            "navbar_mobile_dropdown"
                          );
                          closeMenu();
                        }}
                        style={{ paddingLeft: "2rem" }}
                      >
                        {child.label}
                      </MobileNavButton>
                    ) : (
                      <MobileNavLink
                        key={childIndex}
                        to={child.path}
                        onClick={closeMenu}
                        className={
                          location.pathname === child.path ? "active" : ""
                        }
                        style={{ paddingLeft: "2rem" }}
                      >
                        {child.label}
                      </MobileNavLink>
                    )
                  )}
              </div>
            ) : item.external ? (
              <MobileNavButton
                onClick={() => {
                  handleExternalLink(item.path, item.label, "navbar_mobile");
                  closeMenu();
                }}
              >
                {item.label}
              </MobileNavButton>
            ) : (
              <MobileNavLink
                to={item.path}
                className={location.pathname === item.path ? "active" : ""}
                onClick={closeMenu}
              >
                {item.label}
              </MobileNavLink>
            )}
          </div>
        ))}
      </MobileMenu>
    </Nav>
  );
};

export default Navbar;
